import React, { useEffect, useState } from 'react';
import Loader from '../../components/Loader';
import Paginator from '../../components/Paginator';
import ApiService from '../../services/ApiService';
import { toast } from 'react-toastify';
import { formatDate } from '../../utils/helper';

const currency = process.env.REACT_APP_CURRENCY || 'AED';

// Services a provider / facility added from its app or partner portal. Admin can switch them on/off.
const ServicesTab = ({ companyId }) => {
    const [services, setServices] = useState([]);
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [isLoader, setIsLoader] = useState(false);

    useEffect(() => {
        getServices();
    }, [companyId, page]);

    const getServices = async () => {
        try {
            setIsLoader(true);
            const { data } = await ApiService.request({
                method: 'GET',
                url: `companies/${companyId}/services`,
                data: { page, limit: 10 },
            });
            if (data.status) {
                setServices(data.data);
                setPagination(data.pagination);
            }
        } catch (error) {
            // ApiService already shows the error toast
        } finally {
            setIsLoader(false);
        }
    };

    const toggleService = async (serviceId) => {
        try {
            const { data } = await ApiService.request({ method: 'POST', url: `companies/${companyId}/services/${serviceId}/status` });
            if (data.status) {
                toast.success(data.message);
                setServices(prev => prev.map(s => (s._id === serviceId ? { ...s, status: data.data.status } : s)));
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            // ApiService already shows the error toast
        }
    };

    const price = (service) => {
        const prices = (service.durations || []).map(d => d.discount_price);
        return prices.length ? `from ${currency} ${Math.min(...prices)}` : '-';
    };

    return (
        <div className="tab-pane fade show active">
            <div className="table-responsive">
                <table className="table align-middle table-hover fs-6 gy-5">
                    <thead>
                        <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                            <th>Service</th>
                            <th>Category</th>
                            <th>Price</th>
                            <th>Details</th>
                            <th>Active</th>
                            <th>Created</th>
                        </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600 fs-6">
                        {services.length > 0 ? services.map(service => (
                            <tr key={service._id}>
                                <td>
                                    <div className="d-flex align-items-center">
                                        {service.images?.[0] && (
                                            <div className="symbol symbol-50px me-3">
                                                <img src={service.images[0]} alt={service.name} style={{ objectFit: 'cover' }} />
                                            </div>
                                        )}
                                        <div>
                                            <div className="fw-bold text-dark">{service.name}</div>
                                            <div className="text-muted fs-7 text-truncate" style={{ maxWidth: '260px' }}>{service.short_desc}</div>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div className="text-dark">{service.category_id?.title || '-'}</div>
                                    <div className="text-muted fs-7">{service.sub_category_id?.title}</div>
                                </td>
                                <td className="fw-bold text-dark text-nowrap">{price(service)}</td>
                                <td className="fs-7" style={{ maxWidth: '280px' }}>
                                    {(service.durations || []).map((d, i) => (
                                        <div key={i}>{d.no_hours}h — {currency} {d.discount_price}</div>
                                    ))}
                                </td>
                                <td>
                                    <div className="form-check form-switch form-check-custom form-check-solid">
                                        <input className="form-check-input" type="checkbox" checked={!!service.status}
                                            onChange={() => toggleService(service._id)} />
                                    </div>
                                </td>
                                <td>{formatDate(service.createdAt, 'DD MMM YYYY')}</td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="6" className="text-center text-muted py-8">
                                    {isLoader ? '' : 'No services added yet'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {isLoader && <Loader text="Loading services..." size="30" />}
            {pagination?.total_pages > 1 && (
                <Paginator
                    page={pagination.current_page}
                    rows={pagination.per_page}
                    totalRecords={pagination.total_records}
                    onPageChange={setPage}
                />
            )}
        </div>
    );
};

export default ServicesTab;
